import * as XLSX from 'xlsx';

/**
 * Exports data to Excel file
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Name of the Excel file (without extension)
 * @param {Array} columns - Array of column definitions with {key, header} format
 */
const formatExportData = (data, columns = null) => {
    if (!data || data.length === 0) {
        return [];
    }

    if (columns && columns.length > 0) {
        return data.map((row) => {
            const formattedRow = {};
            columns.forEach((col) => {
                let value = row[col.key];

                if (col.key.includes('.')) {
                    const keys = col.key.split('.');
                    value = keys.reduce((obj, key) => obj?.[key], row);
                }

                if (col.formatter && typeof col.formatter === 'function') {
                    value = col.formatter(value, row);
                }

                formattedRow[col.header] = value ?? '-';
            });
            return formattedRow;
        });
    }

    return data;
};

export const exportToExcel = (data, filename, columns = null) => {
    if (!data || data.length === 0) {
        alert('No data to export');
        return;
    }

    const exportData = formatExportData(data, columns);

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

    // Set column widths
    const colWidths = columns
        ? columns.map((col) => ({ wch: col.width || 15 }))
        : Object.keys(exportData[0] || {}).map(() => ({ wch: 15 }));
    ws['!cols'] = colWidths;

    // Generate Excel file and download
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Give the browser time to start the download before invalidating its URL.
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
};

export const exportToCsv = (data, filename, columns = null) => {
    if (!data || data.length === 0) {
        alert('No data to export');
        return;
    }

    const exportData = formatExportData(data, columns);
    const headers = Object.keys(exportData[0] || {});
    const escapeCsv = (value) => {
        const str = value === null || value === undefined ? '' : String(value);
        return `"${str.replace(/"/g, '""')}"`;
    };

    const rows = exportData.map((row) => headers.map((header) => escapeCsv(row[header])).join(','));
    const csvContent = [headers.map(escapeCsv).join(','), ...rows].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};
